/**
 * Integrytas Group - Base App (MOBILE)
 * Page: CSM.HIERARCHY.LIST — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/ApprovalHierarchyPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + bottom-sheet detail with the ordered
 * approver chain, + an edit sheet for the scalar flags) over the SAME data/logic
 * layer only:
 *   - reads via apiDb (csm_approval_hierarchy + _tl for the display name resolved
 *     against the ACTIVE locale — §8, §17.B4 never a raw id/code — plus
 *     csm_approval_hierarchy_step joined to csm_user_master for the chain),
 *   - the activate/deactivate + set-as-default + requires-manager writes route
 *     through the Common Routine (routedUpdate) — §2 single write path; never a
 *     direct apiDb write. The DB trigger csm_enforce_single_default_hierarchy keeps
 *     at-most-one default, so the switch just toggles the flag.
 * It never re-renders the desktop grid; §16. The approver chain is drag-reorderable
 * on the desktop; here it is shown read-only (mirrors MobileUserEditSheet's roles).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, Network, Star, UserCheck } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import { MobileRelatedChildren, type MobileChildGroup, type MobileChildItem } from '../components/MobileRelatedChildren';
import { buildMobileTranslationsGroup } from '../components/mobile_translations_group';
import {
  MobilePageHeader, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
} from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'CSM.HIERARCHY.LIST';

interface MobileHierarchyRow {
  approval_hierarchy_id: number;
  internal_code: string;
  is_default: 'Y' | 'N';
  is_active: 'Y' | 'N';
  requires_manager_approval: 'Y' | 'N';
  display_text: string;
  step_count: number;
  /** Every locale's text, kept so the related section can list them (§17.B14). */
  tl: TlRow[];
}

interface StepRow {
  approval_hierarchy_step_id: number;
  sequence_order: number;
  user_label: string;
}

type StatusFacet = 'all' | 'active' | 'inactive';

interface TlRow { locale_code: string; display_text: string }

export function MobileApprovalHierarchyPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rows, setRows] = useState<MobileHierarchyRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [defaultOnly, setDefaultOnly] = useState(false);
  const [selected, setSelected] = useState<MobileHierarchyRow | null>(null);
  const [steps, setSteps] = useState<StepRow[]>([]);
  const [stepsBusy, setStepsBusy] = useState(false);
  // Related children (lazy-loaded when the detail sheet opens).
  const [childGroups, setChildGroups] = useState<MobileChildGroup[]>([]);
  const [childBusy, setChildBusy] = useState(false);
  const [editing, setEditing] = useState<MobileHierarchyRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('csm_approval_hierarchy')
        .select(`
          approval_hierarchy_id, internal_code, is_default, is_active,
          requires_manager_approval,
          csm_approval_hierarchy_tl ( locale_code, display_text ),
          csm_approval_hierarchy_step ( approval_hierarchy_step_id, is_active )
        `)
        .order('internal_code');
      if (e) throw e;

      type RawHier = {
        approval_hierarchy_id: number;
        internal_code: string;
        is_default: 'Y' | 'N';
        is_active: 'Y' | 'N';
        requires_manager_approval?: 'Y' | 'N';
        csm_approval_hierarchy_tl?: TlRow[];
        csm_approval_hierarchy_step?: { approval_hierarchy_step_id: number; is_active: 'Y' | 'N' }[];
      };
      const list: MobileHierarchyRow[] = ((data ?? []) as RawHier[]).map((h) => {
        const tls = h.csm_approval_hierarchy_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        const stepRows = h.csm_approval_hierarchy_step ?? [];
        return {
          approval_hierarchy_id: h.approval_hierarchy_id,
          internal_code: h.internal_code,
          is_default: h.is_default,
          is_active: h.is_active,
          requires_manager_approval: h.requires_manager_approval ?? 'N',
          display_text: exact?.display_text ?? fb?.display_text ?? h.internal_code,
          step_count: stepRows.filter((s) => s.is_active === 'Y').length,
          tl: tls,
        };
      });
      setRows(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (the _tl display name
  // follows it — §8).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  // Load the ordered approver chain for the opened hierarchy, resolving each step's
  // user to a name (§17.B4 — never the raw user_id). `t` is intentionally NOT a
  // dependency (it is stable in the app; including it would re-run this effect —
  // which setState()s unconditionally — on every render).
  useEffect(() => {
    if (!selected) { setSteps([]); return; }
    let cancelled = false;
    setStepsBusy(true);
    void (async () => {
      const { data, error: e } = await apiDb
        .from('csm_approval_hierarchy_step')
        .select(`
          approval_hierarchy_step_id, sequence_order, is_active,
          csm_user_master:user_id ( display_name, email )
        `)
        .eq('approval_hierarchy_id', selected.approval_hierarchy_id)
        .eq('is_active', 'Y')
        .order('sequence_order');
      if (cancelled) return;
      if (e) { setError(e.message); setSteps([]); setStepsBusy(false); return; }
      type RawStep = {
        approval_hierarchy_step_id: number;
        sequence_order: number;
        csm_user_master?: { display_name?: string; email?: string } | null;
      };
      const chain: StepRow[] = ((data ?? []) as RawStep[]).map((s) => ({
        approval_hierarchy_step_id: s.approval_hierarchy_step_id,
        sequence_order: s.sequence_order,
        user_label: s.csm_user_master?.display_name || s.csm_user_master?.email || t('common.unknown', 'Unknown'),
      }));
      setSteps(chain);
      setStepsBusy(false);
    })();
    return () => { cancelled = true; };
    // `t` intentionally omitted — see MobileBulkImportPage: it only supplies a
    // fallback label here, and depending on it can loop when t is unstable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Lazy-load the selected hierarchy's children when the detail sheet opens: the
  // ordered approval steps (approver + sequence badge) and the job descriptions
  // that reference it. Names resolved via _tl against the active locale (§17.B4 —
  // never the raw user_id/code).
  useEffect(() => {
    if (!selected) { setChildGroups([]); setChildBusy(false); return; }
    const hierarchyId = selected.approval_hierarchy_id;
    let cancelled = false;
    setChildBusy(true);
    void (async () => {
      const one = <T,>(v: T | T[] | undefined): T | undefined => (Array.isArray(v) ? v[0] : v);
      const pickTl = (tls: TlRow[] | undefined, fb: string) => {
        const list = tls ?? [];
        return list.find((x) => x.locale_code === activeLocale)?.display_text
          ?? list.find((x) => x.locale_code === 'EN_US')?.display_text
          ?? list[0]?.display_text ?? fb;
      };
      const byLabel = (a: MobileChildItem, b: MobileChildItem) => a.label.localeCompare(b.label);
      try {
        const [stp, jbs] = await Promise.all([
          apiDb.from('csm_approval_hierarchy_step')
            .select('approval_hierarchy_step_id, sequence_order, user_id, csm_user_master ( display_name, email )')
            .eq('approval_hierarchy_id', hierarchyId)
            .order('sequence_order'),
          apiDb.from('csm_job_description')
            .select('job_description_id, internal_code, csm_job_description_tl ( locale_code, display_text )')
            .eq('approval_hierarchy_id', hierarchyId),
        ]);

        type UserEmbed = { display_name?: string; email?: string };
        type StepChildRow = { approval_hierarchy_step_id: number; sequence_order: number; user_id: number; csm_user_master?: UserEmbed | UserEmbed[] };
        const stepItems: MobileChildItem[] = ((stp.data ?? []) as StepChildRow[]).map((r) => {
          const u = one(r.csm_user_master);
          return {
            id: r.approval_hierarchy_step_id,
            label: u?.display_name ?? u?.email ?? `#${r.user_id}`,
            sub: u?.email,
            badges: [String(r.sequence_order)],
          };
        }).sort(byLabel);

        type JobEmbed = { internal_code: string; csm_job_description_tl?: TlRow[] };
        type JobRow = { job_description_id: number; internal_code: string; csm_job_description_tl?: TlRow[] };
        const jobItems: MobileChildItem[] = ((jbs.data ?? []) as JobRow[]).map((r) => {
          const j: JobEmbed = { internal_code: r.internal_code, csm_job_description_tl: r.csm_job_description_tl };
          return { id: r.job_description_id, label: pickTl(j.csm_job_description_tl, j.internal_code), sub: j.internal_code };
        }).sort(byLabel);

        if (cancelled) return;
        setChildGroups([
          { key: 'steps', table: 'csm_approval_hierarchy_step', pkColumn: 'approval_hierarchy_step_id', title: t('mobile.approvals.related.steps', 'Approval Steps'), items: stepItems },
          { key: 'jobs', table: 'csm_job_description', pkColumn: 'job_description_id', title: t('mobile.approvals.related.jobs', 'Job Descriptions'), items: jobItems },
          buildMobileTranslationsGroup(selected.tl, t, activeLocale),
        ]);
      } catch {
        if (!cancelled) setChildGroups([]);
      } finally {
        if (!cancelled) setChildBusy(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, activeLocale]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (defaultOnly && r.is_default !== 'Y') return false;
      if (!q) return true;
      return r.display_text.toLowerCase().includes(q) || r.internal_code.toLowerCase().includes(q);
    });
  }, [rows, filter, statusFacet, defaultOnly]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.hierarchies.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const approversLabel = (n: number) =>
    t('mobile.hierarchies.approvers_count', { count: n, defaultValue: `${n} approvers` });

  const toggleActive = async (r: MobileHierarchyRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'csm_approval_hierarchy', 'approval_hierarchy_id', r.approval_hierarchy_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.hierarchies.title', 'Approval Hierarchies')}
        description={t('mobile.hierarchies.description', 'Ordered approver chains available to the approval engine.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.hierarchies.search', 'Search hierarchies')}
          aria-label={t('mobile.hierarchies.search', 'Search hierarchies')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={[{ value: 'default', label: t('mobile.hierarchies.default_only', 'Default') }]}
        isActive={() => defaultOnly}
        onSelect={() => setDefaultOnly((v) => !v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.hierarchies.none', 'No hierarchies match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.approval_hierarchy_id}
              lead={<Network />}
              leadTone={r.is_default === 'Y' ? 'blue' : 'slate'}
              title={r.display_text}
              subtitle={
                `${r.internal_code} · ${approversLabel(r.step_count)}`
                + (r.is_default === 'Y' ? ' · ' + t('mobile.hierarchies.default', 'Default') : '')
              }
              trailing={
                <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                  {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                </MobileStatusPill>
              }
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.hierarchies.detail', 'Approval hierarchy detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Network size={20} /></span>
              <span>
                <div className="m-nm">{selected.display_text}</div>
                <div className="m-em">{selected.internal_code}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.hierarchies.status', 'Status')}
                value={<MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>{selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}</MobileStatusPill>}
              />
              <MobileDetailRow
                label={t('mobile.hierarchies.default', 'Default')}
                value={selected.is_default === 'Y'
                  ? <MobileStatusPill tone="ok">{t('mobile.hierarchies.default', 'Default')}</MobileStatusPill>
                  : t('common.standard', 'Standard')}
              />
              <MobileDetailRow
                label={t('mobile.hierarchies.requires_manager', 'Manager approval')}
                value={<MobileStatusPill tone={selected.requires_manager_approval === 'Y' ? 'info' : 'no'}>{selected.requires_manager_approval === 'Y' ? t('common.boolean.yes', 'Yes') : t('common.boolean.no', 'No')}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.hierarchies.approvers', 'Approvers')} value={approversLabel(selected.step_count)} />
            </MobileContentCard>

            {/* Ordered approver chain (read-only — reorder on desktop). */}
            <div className="m-sheet-h">{t('mobile.hierarchies.chain', 'Approver chain')}</div>
            <MobileContentCard>
              {stepsBusy ? (
                <div className="m-empty">{t('common.loading', 'Loading…')}</div>
              ) : steps.length === 0 ? (
                <div className="m-empty"><UserCheck /> {t('mobile.hierarchies.chain_empty', 'No approvers in this chain')}</div>
              ) : (
                steps.map((s, idx) => (
                  <MobileDetailRow
                    key={s.approval_hierarchy_step_id}
                    label={t('mobile.hierarchies.step', 'Step {{n}}', { n: idx + 1 })}
                    value={s.user_label}
                  />
                ))
              )}
            </MobileContentCard>

            <MobileRelatedChildren groups={childGroups} busy={childBusy} session={session} />

            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.hierarchies.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy || selected.is_default === 'Y'}
                title={selected.is_default === 'Y' ? t('mobile.hierarchies.default_locked', 'The default hierarchy cannot be deactivated') : undefined}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileHierarchyEditSheet
        session={session}
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the master-table scalar flags (Active + Set as default + Requires
// manager approval), saved through the Common Routine (routedUpdate) — §2. The
// display name lives in the _tl table and the approver chain is drag-reordered on
// the desktop, so both are shown read-only here (mirrors MobileOrgEditSheet).
// ---------------------------------------------------------------------------

interface MobileHierarchyEditSheetProps {
  session: AuthSession;
  row: MobileHierarchyRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function MobileHierarchyEditSheet({ session, row, onClose, onSaved }: MobileHierarchyEditSheetProps) {
  const { t } = useTranslation();
  const [active, setActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [requiresManager, setRequiresManager] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different row is opened.
  useEffect(() => {
    if (!row) return;
    setActive(row.is_active === 'Y');
    setIsDefault(row.is_default === 'Y');
    setRequiresManager(row.requires_manager_approval === 'Y');
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks). The DB
    // trigger csm_enforce_single_default_hierarchy clears any prior default.
    const { error: e } = await routedUpdate(session, 'csm_approval_hierarchy', 'approval_hierarchy_id', row.approval_hierarchy_id, {
      is_active: active ? 'Y' : 'N',
      is_default: isDefault ? 'Y' : 'N',
      requires_manager_approval: requiresManager ? 'Y' : 'N',
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.hierarchies.edit_sheet', 'Edit approval hierarchy')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Star size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.hierarchies.edit_title', 'Edit hierarchy')}</div>
                <div className="m-em">{row.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.hierarchies.name', 'Name')}</label>
                <div className="m-formrow-ro">
                  {row.display_text}
                  <span className="m-formrow-hint"> · {t('mobile.hierarchies.name_desktop', 'edit on desktop')}</span>
                </div>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.hierarchies.active_hint', 'Available for assignment')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.hierarchies.set_default', 'Set as default')}</div>
                  <div className="m-formrow-s">{t('mobile.hierarchies.default_hint', 'Only one hierarchy can be the default')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={isDefault}
                  className={'m-switch ' + (isDefault ? 'm-on' : 'm-off')}
                  onClick={() => setIsDefault((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('mobile.hierarchies.requires_manager', 'Manager approval')}</div>
                  <div className="m-formrow-s">{t('mobile.hierarchies.requires_manager_hint', "Require the requester's manager to approve first")}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={requiresManager}
                  className={'m-switch ' + (requiresManager ? 'm-on' : 'm-off')}
                  onClick={() => setRequiresManager((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.hierarchies.chain', 'Approver chain')}</label>
                <div className="m-formrow-ro">
                  {t('mobile.hierarchies.approvers_count', { count: row.step_count, defaultValue: `${row.step_count} approvers` })}
                  <span className="m-formrow-hint"> · {t('mobile.hierarchies.chain_desktop', 'reorder on desktop')}</span>
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
